import type { FeatureItem } from '../types/features.types'
import type { LandingContent } from '../types/landingContent.types'
import type { EliteConsentTextItem } from '../../../types/misc.types'
import { ElitePrimeAllocationCategoryName } from '../../../types/elitePrimeSummary.types'
import { ELITE_PRIME_UPGRADE_ADJUSTMENT_INFO, ELITE_PRIME_UPGRADE_MAX_SLAB_INFO } from '../constants/ctaCopy'

const kycConsentTextList: EliteConsentTextItem[] = [
  {
    consentText:
      'I consent to Uniorbit Wealth Management Pvt Ltd accessing and verifying my KYC records from the SEBI-registered KRA using my PAN, or from Digilocker using my Aadhaar if KRA records are unavailable.'
  },
  {
    consentText: 'Investment Advisory Agreement.',
    ctaUrl: 'https://www.powerup.money/documents?docsType=POWERUP_MONEY_WEALTH_MANAGEMENT_PRIVATE_LIMITED'
  }
]

const eliteOfferings: FeatureItem[] = [
  {
    id: 'power-rank',
    iconSrc: '/images/elite-plans/elite-offerings/power-rank.svg',
    title: 'Power Rank',
    description: 'Know how every fund ranks within its category.'
  },
  {
    id: 'rebalance-plan-feat',
    iconSrc: '/images/elite-plans/elite-offerings/rebalance-plan.svg',
    title: 'Rebalance Plan',
    description: 'Exit underperforming funds and switch to stronger alternatives.'
  },
  {
    id: 'power-select',
    iconSrc: '/images/elite-plans/elite-offerings/power-select.svg',
    title: 'Power Select',
    description: 'Compare funds easily across rank, returns and risk.'
  },
  {
    id: 'tax-summary',
    iconSrc: '/images/elite-plans/elite-offerings/tax-summary.svg',
    title: 'Tax Summary & Plan',
    description: 'See how much taxes you owe, and utilise your tax benefits efficiently'
  },
  {
    id: 'global-funds',
    iconSrc: '/images/elite-plans/elite-offerings/global-funds.svg',
    title: 'Global Funds',
    description: 'Discover global funds currently available for investment.'
  },
  {
    id: 'regular-to-direct',
    iconSrc: '/images/elite-plans/elite-offerings/regular-to-direct.svg',
    title: 'Regular to Direct',
    description: 'Switch to funds with lower expense ratios, save more returns'
  },
  {
    id: 'elite-less',
    iconSrc: '/images/elite-plans/elite-offerings/elite-lens.svg',
    title: 'Elite Lens',
    description: 'Weekly newsletter with institutional-grade research on markets, funds and investing.'
  }
]

export const localLandingContent: LandingContent = {
  contentVersion: '1.0.0-local',
  planOrder: ['elite', 'elite-prime'],
  compareTitle: {
    leading: 'Compare Free',
    connector: 'vs',
    trailing: 'Elite'
  },
  compareSections: [
    {
      id: 'portfolio-strategy',
      title: 'PORTFOLIO STRATEGY',
      rows: [
        {
          id: 'allocation-strategy',
          label: 'Allocation strategy',
          benefitTabTitle: 'Allocation Strategy',
          prime: 'check',
          elite: 'lock',
          free: 'lock'
        },
        {
          id: 'allocation-plan',
          label: 'Allocation plan',
          benefitTabTitle: 'Power Allocation',
          prime: 'check',
          elite: 'lock',
          free: 'lock'
        },
        {
          id: 'sip-strategy',
          label: 'SIP Strategy',
          benefitTabTitle: 'SIP Strategy',
          prime: 'check',
          elite: 'lock',
          free: 'lock'
        },
        {
          id: 'lumpsum-investment-plan',
          label: 'Lumpsum strategy',
          benefitTabTitle: 'Lumpsum Investment Plan',
          prime: 'check',
          elite: 'lock',
          free: 'lock'
        },
        {
          id: 'withdrawal-strategy',
          label: 'Withdrawal strategy',
          benefitTabTitle: 'Withdrawal Strategy',
          prime: 'check',
          elite: 'lock',
          free: 'lock'
        }
      ]
    },
    {
      id: 'portfolio-rebalancing',
      title: 'PORTFOLIO REBALANCING',
      rows: [
        {
          id: 'power-rebalance',
          label: 'Power Rebalance',
          benefitTabTitle: 'Power Rebalance',
          prime: 'check',
          elite: 'check',
          free: 'lock'
        },
        {
          id: 'regular-to-direct',
          label: 'Regular to Direct Plan',
          benefitTabTitle: 'Regular to Direct plan',
          prime: 'check',
          elite: 'check',
          free: 'lock'
        }
      ]
    },
    {
      id: 'fund-selection',
      title: 'FUND SELECTION & REVIEW',
      rows: [
        {
          id: 'power-rank',
          label: 'Power Rank',
          benefitTabTitle: 'Power Rank',
          prime: 'check',
          elite: 'check',
          free: 'lock'
        },
        {
          id: 'power-select',
          label: 'Power Select',
          benefitTabTitle: 'Power Select',
          prime: 'check',
          elite: 'check',
          free: 'lock'
        },
        {
          id: 'rank-trends',
          label: 'Rank Trends',
          benefitTabTitle: 'Rank Trends',
          prime: 'check',
          elite: 'check',
          free: 'lock'
        }
      ]
    },
    {
      id: 'high-quality-research',
      title: 'HIGH QUALITY RESEARCH',
      rows: [
        {
          id: 'elite-lens',
          label: 'Elite Lens',
          benefitTabTitle: 'Elite Lens',
          prime: 'check',
          elite: 'check',
          free: 'lock'
        }
      ]
    },
    {
      id: 'tax',
      title: 'TAX',
      rows: [
        {
          id: 'tax-saving-plan',
          label: 'Tax Saving Plan',
          benefitTabTitle: 'Tax Saving Plan',
          prime: 'check',
          elite: 'check',
          free: 'lock'
        }
      ]
    }
  ],
  plans: {
    elite: {
      id: 'elite',
      tabLabel: 'Elite',
      themeId: 'elite',
      hero: {
        emblemSrc: '/images/elite-plans/elite/elite-logo.png',
        emblemRiveSrc: '/rives/elite-plans/Elite.riv',
        emblemRiveDarkSrc: '/rives/elite-plans/Elite_Hero_Asset_Dark_Theme.riv',
        emblemAlt: 'Elite',
        headlineSans: 'Manage portfolio',
        headlineSerif: 'like the top 1%',
        subcopy: 'From selection, review to rebalancing - make mutual fund investing effortless with Elite'
      },
      trustItems: [
        {
          value: 'SEBI',
          label: 'Registered',
          iconUrl: '/images/new-elite/stats/sebi.svg',
          iconClassName: 'size-[18px]'
        },
        {
          value: '1,00,000+',
          label: 'Elite users',
          iconUrl: '/images/new-elite/stats/user.svg',
          iconClassName: 'size-[18px]'
        },
        {
          value: '₹50,000+ Cr',
          label: 'Elite AUM',
          iconUrl: '/images/new-elite/stats/aum.svg',
          iconClassName: 'size-[18px]'
        }
      ],
      cta: { label: 'Get Elite at ₹999/yr+GST' },
      consentTextList: kycConsentTextList,
      lockInPriceBanner: {
        assuranceTitle: 'Price increasing soon',
        title: 'Lock in your price',
        pricePrefix: 'today @ ',
        // Per-day bullet is filled from live fee (same keys as EliteLandingJoinOfferStrip)
        bullets: ['Lowest price in the market', 'Available for a limited time'],
        artSrc: '/images/elite-plans/elite/lock-in-price/art.png',
        detailsLabel: 'View Pricing details',
        philosophyLabel: 'Our pricing philosophy',
        pricingDetails: {
          logoSrc: '/images/elite-plans/elite/pricing-details/logo.png',
          logoAlt: 'Elite',
          eyebrow: 'ELITE PRICING',
          priceSuffix: '/yr+GST',
          assuranceBadge: 'Lowest price in the market',
          portfolioValueLabel: 'Your portfolio value',
          slabRateLabel: 'Slab rate applied',
          slabPortfolioColumnLabel: 'Portfolio Value',
          slabPriceColumnLabel: 'Elite Price',
          slabFooterNote: 'Price to increase soon, lock in your price today',
          lockNote: 'Once you get Elite, this price stays locked till membership expiry - even if your portfolio grows',
          listPrice: '₹1,999'
        }
      },
      insightBanner: {
        statusLabel: 'Your plans are ready',
        title: 'Your portfolio needs fixing'
      },
      // Card set lives in adviceCardCatalog — not listed here / not CMS-driven
      adviceCards: [],
      featuresTitle: {
        line1: 'Everything Elite',
        line2: 'has to offer'
      },
      features: eliteOfferings,
      testimonialsTitle: {
        line1: 'Hear it from our',
        line2: '1,00,000 Elite members'
      },
      testimonials: [
        {
          id: 'patrick',
          quote: '<b>Power Rebalancing</b> is a very powerful feature and the output was very precise and accurate.',
          name: 'Patrick Addai'
        },
        {
          id: 'nitin',
          quote: 'PowerUp app is intuitive and so intelligent, and the <b>Elite plan is on a whole new level.</b>',
          name: 'Nitin J'
        },
        {
          id: 'ramesh',
          quote: 'The <b>Power Rank</b> features are excellent in identifying Equity Mutual Funds.',
          name: 'Ramesh K'
        }
      ]
    },
    'elite-prime': {
      id: 'elite-prime',
      tabLabel: 'Elite Prime',
      themeId: 'elitePrime',
      hero: {
        emblemSrc: '/images/elite-plans/elite-prime/elite-prime-logo.png',
        emblemRiveSrc: '/rives/elite-plans/Prime.riv',
        emblemRiveDarkSrc: '/rives/elite-plans/Prime_Hero_Asset_Dark_Theme.riv',
        emblemAlt: 'Elite Prime',
        badge: 'Upgraded tier of Elite',
        headlineSans: 'Your end-to-end',
        headlineSerif: 'Wealth Manager',
        subcopy:
          'From assessing your risk profile to recommending the right asset allocation, SIP plan, fresh lump sums and withdrawals — Prime manages your wealth end-to-end.'
      },
      trustItems: [
        {
          value: 'SEBI',
          label: 'Registered',
          iconUrl: '/images/elite-plans/elite-prime/cvp/sebi.svg',
          iconClassName: 'size-[18px]'
        },
        {
          value: "One of India's",
          label: 'Largest RIA',
          iconUrl: '/images/elite-plans/elite-prime/cvp/largest-ria.svg',
          iconClassName: 'h-[18px] w-[47px]'
        },
        {
          value: '₹50,000+ Cr',
          label: 'Elite AUM',
          iconUrl: '/images/elite-plans/elite-prime/cvp/aum.svg',
          iconClassName: 'size-[18px]'
        }
      ],
      cta: { label: 'Get Prime at ₹4,999/yr + GST' },
      consentTextList: kycConsentTextList,
      plusBanner: {
        title: 'Lowest Price Assurance',
        detailsLabel: 'View Pricing details',
        philosophyLabel: 'Our pricing philosophy',
        items: [
          {
            id: 'elite-prime',
            label: 'Elite Prime',
            sublabel: '1 year membership',
            tone: 'emphasis',
            showLeaves: true
          },
          {
            id: 'market',
            label: 'Market pricing',
            sublabel: 'via regular funds',
            tone: 'muted'
          }
        ],
        pricingDetails: {
          logoSrc: '/images/elite-plans/elite-prime/elite-prime-logo.png',
          logoAlt: 'Elite Prime',
          eyebrow: 'ELITE PRIME PRICING',
          priceSuffix: '/yr+GST',
          rows: [
            {
              id: 'portfolio-value',
              label: 'Your portfolio value'
            },
            {
              id: 'slab-rate',
              label: 'Slab rate applied',
              showChevron: true,
              slabTable: {
                portfolioValueLabel: 'Portfolio Value',
                primeRateLabel: 'Prime Rate',
                maxFeeLabel: 'Max Fee'
              }
            },
            {
              id: 'elite-prime-fee',
              label: 'Elite Prime fee',
              upgradeOnly: true
            },
            {
              id: 'adjusted-max-slab',
              label: 'Adjusted fee for max slab',
              upgradeOnly: true,
              showInfo: true,
              showDividerBefore: true,
              valueMuted: true,
              note: ELITE_PRIME_UPGRADE_MAX_SLAB_INFO
            },
            {
              id: 'adjusted-remaining',
              label: 'Adjusted remaining Elite plan',
              upgradeOnly: true,
              showInfo: true,
              showDividerBefore: true,
              note: ELITE_PRIME_UPGRADE_ADJUSTMENT_INFO
            },
            {
              id: 'pricing-rounded',
              label: 'Pricing (Rounded off)'
            }
          ],
          lockNote: 'Once you get Prime, your price remains locked for a year even when your portfolio value increases',
          marketEyebrow: 'MARKET PRICING',
          marketPriceSuffix: '/yr',
          marketBreakdown: {
            note: 'Investment advisory is offered via Regular Mutual funds in the market which have higher expense ratio. The higher expense ratio has fee baked in for the investment advisor. Below is the estimate of market pricing for your portfolio.',
            holdingsLabel: 'Your portfolio holdings',
            chargesLabel: 'Additional charges',
            totalLabel: 'Total'
          }
        }
      },
      allocationPreview: {
        title: 'Your current allocation needs fixing',
        statusLabel: 'Your allocation strategy is ready',
        statusColor: 'var(--ep-accent)',
        allocationToggleLabel: 'Allocation',
        fundCountToggleLabel: 'Fund count',
        corePortfolioLabel: 'Core Portfolio',
        otherPortfoliosLabel: 'Other Portfolios',
        viewDetailsLabel: 'View details',
        findStrategy: {
          sectionTitle: 'Find your strategy',
          notInitiated: {
            prefix: 'Find ',
            emphasis: 'how much you should invest in each category,',
            suffix: ' based on your life stage and risk appetite.',
            ctaLabel: 'Find your strategy'
          },
          inProgress: {
            emphasis: "You're almost there.",
            body: ' Discover your strategy and learn how much to invest in each category.',
            ctaLabel: 'Find your strategy'
          },
          submitted: {
            title: 'Your plan will be ready in {planAvailableInDays}.',
            body: 'Our experts are currently reviewing your portfolio',
            viewDetailsLabel: 'View details'
          }
        },
        categoryItems: [
          {
            id: 'high-risk',
            categoryName: ElitePrimeAllocationCategoryName.HIGH_RISK,
            color: '#1470CC'
          },
          {
            id: 'medium-risk',
            categoryName: ElitePrimeAllocationCategoryName.MEDIUM_RISK,
            color: '#4D9FE8'
          },
          {
            id: 'low-risk',
            categoryName: ElitePrimeAllocationCategoryName.LOW_RISK,
            color: '#A9D3F5'
          },
          {
            id: 'gold-silver',
            categoryName: ElitePrimeAllocationCategoryName.SILVER_AND_GOLD,
            color: '#E8B36A'
          },
          {
            id: 'global-us',
            categoryName: ElitePrimeAllocationCategoryName.GLOBAL_US,
            color: '#A99BE8'
          },
          {
            id: 'hybrid',
            categoryName: ElitePrimeAllocationCategoryName.HYBRID,
            color: '#7FD4C1'
          }
        ]
      },
      insightBanner: {
        statusLabel: 'Your allocation plans are ready',
        statusColor: 'var(--ep-accent)',
        title: 'Your portfolio needs fixing',
        findStrategy: {
          title: 'Find strategy to get asset\nallocation plans'
        },
        submitted: {
          statusLabel: 'In progress',
          title: 'Your allocation plans\nare getting ready'
        }
      },
      // Card set lives in adviceCardCatalog — not listed here / not CMS-driven
      adviceCards: [],
      featuresTitle: {
        line1: 'Everything Elite Prime',
        line2: 'has to offer'
      },
      features: [
        {
          id: 'strategy-planning',
          iconSrc: '/images/elite-plans/elite-prime-offerings/strategy-planning.svg',
          title: 'Allocation Strategy',
          description: 'Get your own investing strategy, built for your life stage and risk appetite.'
        },
        {
          id: 'allocation-plan-feat',
          iconSrc: '/images/elite-plans/elite-prime-offerings/allocation-plan.svg',
          title: 'Allocation Plan',
          description: 'Optimise your entire portfolio as per your life stage and risk appetite.'
        },
        {
          id: 'sip-plan-feat',
          iconSrc: '/images/elite-plans/elite-prime-offerings/sip-plan.svg',
          title: 'SIP Plan',
          description: 'Invest in a mix of funds every month to maintain your ideal allocation.'
        },
        {
          id: 'gold-silver-global',
          iconSrc: '/images/elite-plans/elite-prime-offerings/gold-silver-global-advisory.svg',
          title: 'Gold, Silver, Global Advisory',
          description: 'Know how much gold, silver & global exposure your portfolio needs.'
        },
        {
          id: 'lumpsum-investment-strategy',
          iconSrc: '/images/elite-plans/elite-prime-offerings/withdrawal-strategy.svg',
          title: 'Lumpsum Investment Plan',
          description: 'See where and how you can invest your lump sum amount intelligently.'
        },
        {
          id: 'withdrawal-strategy',
          iconSrc: '/images/elite-plans/elite-prime-offerings/lumpsum-investment-strategy.svg',
          title: 'Withdrawal Strategy',
          description: 'Withdraw what you need without taking a hit from high taxes and exit load.',
          comingSoon: true
        },
        {
          id: 'advisor-support-feat',
          iconSrc: '/images/elite-plans/elite-prime-offerings/advisor-support.svg',
          title: 'Access to Investment Coach',
          description: 'Ask any doubts about your portfolio, anytime.'
        }
      ],
      featuresAfterCompare: eliteOfferings,
      testimonialsTitle: {
        line1: 'Hear it from our',
        line2: '1,00,000 Elite members'
      },
      testimonials: [
        {
          id: 'anita',
          quote:
            'Elite Prime feels like having a wealth manager who actually explains the <b>why behind every move.</b>',
          name: 'Anita S'
        },
        {
          id: 'vikram',
          quote: 'Family accounts and allocation guidance finally made our portfolio make sense as a <b>household.</b>',
          name: 'Vikram P'
        },
        {
          id: 'meera',
          quote: 'The private markets and global coverage are what set <b>Prime apart</b> for us.',
          name: 'Meera R'
        }
      ]
    }
  }
}
